<script setup lang="ts">
/*
* Компонент EmployeeProfileSidebar - боковая панель профиля сотрудника
*
* Основные функции:
* - Отображение детальной информации о сотруднике (ФИО, должность, отделение, контакты)
* - Показ статуса онлайн/оффлайн сотрудника
* - Предоставление быстрых действий: написание в чат, просмотр контактов
* - Поддержка адаптивного дизайна для мобильных устройств
*
* Особенности:
* - Загружает данные сотрудника по ID при открытии
* - Отображает аватар с индикатором онлайн-статуса
* - Предоставляет кликабельные контакты (email, телефон)
* - Интегрируется с системой чатов через openChat
*/

// Импорты Vue и Vuetify
import { computed, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
// Импорты утилит
import { formatDateOnly, getFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import { openOrCreateChat } from '@/utils/openChat'

// Импорты хранилищ
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

// Импорты типов
import type { ICurrentEmployee } from '@/modules/apiStore/types/employees/ICurrentEmployee'

// Определение props компонента
interface Props {
  employeeId: string // ID сотрудника для загрузки данных
  modelValue: boolean // Флаг видимости компонента (v-model)
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean] // Событие обновления видимости
}>()

// Инициализация утилит Vuetify
const { smAndDown } = useDisplay()

// Инициализация хранилищ
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()

// Реактивные состояния компонента
// Локальное состояние видимости компонента
const isOpen = ref(false)

// Данные загруженного сотрудника
const employeeData = ref<ICurrentEmployee | null>(null)

// Флаг загрузки данных
const loading = ref(false)

/**
 * Обработчик закрытия компонента
 *
 * Назначение:
 * - Закрывает боковую панель профиля сотрудника
 * - Эмитит событие обновления для синхронизации с v-model
 * - Сбрасывает локальное состояние видимости
 */
const handleClose = () => {
  isOpen.value = false
  emit('update:modelValue', false)
}

/**
 * Обработчик открытия чата с сотрудником
 *
 * Назначение:
 * - Инициирует открытие чата с текущим сотрудником
 * - Использует утилиту openChat для интеграции с системой чатов
 * - Передает ID сотрудника для создания/открытия чата
 */
const handleChat = async () => {
  if (!employeeData.value?.id) {
    return
  }

  feedbackStore.isGlobalLoading = true
  try {
    // Открываем скользящий чат с сотрудником, сразу передавая уже известные
    // данные (ФИО, телефон, аватар), чтобы в списке чатов не отображался номер телефона
    await openOrCreateChat(employeeData.value.id, false, {
      fullName: getFullName(employeeData.value),
      phone: employeeData.value.phone_number ?? undefined,
      avatar: employeeData.value.avatar,
    })

    // Закрываем сайдбар профиля сотрудника
    handleClose()
  } catch (error) {
    console.error('Failed to open chat:', error)
  } finally {
    feedbackStore.isGlobalLoading = false
  }
}

/**
 * Вычисляемое свойство для получения аватара сотрудника
 *
 * Назначение:
 * - Возвращает аватар сотрудника если он установлен
 * - Генерирует случайный аватар если аватар не установлен
 * - Обеспечивает всегда доступное изображение для отображения
 *
 * Возвращает:
 * - string: URL аватара сотрудника или сгенерированного аватара
 */
const getAvatar = computed(() => {
  if (employeeData.value?.avatar) {
    return employeeData.value.avatar
  }
  return getRandomAvatar()
})

/**
 * Загрузка данных сотрудника по ID
 *
 * Назначение:
 * - Выполняет API-запрос для получения детальной информации о сотруднике
 * - Обновляет состояние loading во время выполнения запроса
 * - Сохраняет полученные данные в employeeData
 * - Обрабатывает ошибки загрузки данных
 *
 * Алгоритм:
 * 1. Проверяет наличие ID сотрудника
 * 2. Устанавливает флаг загрузки
 * 3. Выполняет запрос к API через apiStore.fetchEmployeeById
 * 4. Сохраняет полученные данные
 * 5. Сбрасывает флаг загрузки независимо от результата
 *
 * Входные данные:
 * - id: string - идентификатор сотрудника для загрузки
 */
const fetchEmployeeData = async (id: string) => {
  if (!id) return

  loading.value = true
  try {
    employeeData.value = await apiStore.fetchEmployeeById(id)
  } catch (error) {
    console.error('Error fetching employee data:', error)
  } finally {
    loading.value = false
  }
}

/**
 * Вотчер для отслеживания изменения modelValue (v-model)
 *
 * Назначение:
 * - Синхронизирует локальное состояние isOpen с внешним modelValue
 * - Автоматически загружает данные сотрудника при открытии компонента
 *
 * Логика:
 * - При изменении modelValue на true и наличии employeeId загружает данные
 * - Обновляет локальное состояние видимости компонента
 */
watch(() => props.modelValue, newValue => {
  isOpen.value = newValue
  if (newValue && props.employeeId) {
    fetchEmployeeData(props.employeeId)
  }
})

/**
 * Вотчер для отслеживания изменения employeeId
 *
 * Назначение:
 * - Автоматически перезагружает данные сотрудника при изменении ID
 * - Обеспечивает актуальность данных при смене отображаемого сотрудника
 *
 * Логика:
 * - Срабатывает только когда компонент открыт (isOpen.value = true)
 * - Вызывает fetchEmployeeData с новым ID сотрудника
 */
watch(() => props.employeeId, newId => {
  if (isOpen.value && newId) {
    fetchEmployeeData(newId)
  }
})

/**
 * Хук монтирования компонента
 *
 * Выполняет:
 * - Если компонент монтируется в открытом состоянии и указан employeeId
 * - Немедленно загружает данные сотрудника для первоначального отображения
 */
onMounted(() => {
  if (props.modelValue && props.employeeId) {
    fetchEmployeeData(props.employeeId)
  }
})
</script>

<template>
  <VDialog
    v-if="!loading"
    v-model="isOpen"
    persistent
    class="employee-dialog"
    :width="smAndDown ? '100%' : 370"
    :z-index="2500"
  >
    <VCard
      class="employee-sidebar w-100"
      height="100vh"
    >
      <!-- Header -->
      <VCardTitle class="d-flex align-center justify-space-between pa-4">
        <div class="d-flex align-center gap-2">
          <VChip
            v-if="employeeData?.is_manager"
            color="primary"
            class="d-flex flex-row justify-center align-center px-3 py-0.5 gap-1"
            size="small"
          >
            Руководитель
          </VChip>
        </div>

        <CustomCloseButton :close-action="handleClose" />
      </VCardTitle>

      <!-- Content -->
      <VCardText class="pa-4">
        <!-- Avatar Section -->
        <div class="d-flex flex-column align-center mb-6">
          <VBadge
            dot
            location="bottom right"
            offset-x="3"
            offset-y="3"
            color="success"
            :model-value="employeeData?.online"
          >
            <VAvatar :image="getAvatar" variant="tonal" size="80" />
          </VBadge>

          <!-- Full Name -->
          <div class="text-h6 mt-4 text-center text-h5">
            {{ employeeData ? getFullName(employeeData) : '' }}
          </div>

          <!-- Position -->
          <div v-if="employeeData?.position?.name" class="body-1 mt-1 text-center opacity-70">
            {{ employeeData?.position?.name }}
          </div>
        </div>

        <!-- Department -->
        <div class="mb-4">
          <div class="body-1 title">
            Отделение
          </div>
          <div class="body-1 subtitle">
            {{ employeeData?.department?.name || 'Не указано' }}
          </div>
        </div>

        <!-- Birth Date -->
        <div class="mb-4">
          <div class="title">
            Дата рождения
          </div>
          <div class="body-1 subtitle">
            {{ formatDateOnly(employeeData?.birth_date) || 'Не указано' }}
          </div>
        </div>

        <!-- Contacts -->
        <div class="mb-6">
          <div class="mb-2 title">
            Контакты
          </div>

          <!-- Email -->
          <div class="d-flex align-center gap-2 mb-2 ">
            <VIcon icon="ri-mail-line" size="18" />
            <a
              v-if="employeeData?.email"
              :href="`mailto:${employeeData?.email}`"
              class="body-1 contacts text-decoration-none"
              @click.stop
            >
              {{ employeeData?.email }}
            </a>
            <span v-else class="body-1 contacts">Не указано</span>
          </div>

          <!-- Phone (Personal) -->
          <div class="d-flex align-center gap-2 subtitle mb-2">
            <VIcon icon="ri-smartphone-line" size="18" />
            <a
              v-if="employeeData?.phone_number"
              :href="`tel:${employeeData?.phone_number}`"
              class="body-1 text-decoration-none contacts"
              @click.stop
            >
              {{ employeeData?.phone_number }} <span class="text-medium-emphasis">- личный</span>
            </a>
            <span v-else class="body-1">Личный: Не указано</span>
          </div>

          <!-- Phone (Work) -->
          <div class="d-flex align-center gap-2 subtitle">
            <VIcon icon="ri-phone-line" size="18" />
            <a
              v-if="employeeData?.work_phone_number"
              :href="`tel:${employeeData?.work_phone_number}`"
              class="body-1 text-decoration-none contacts"
              @click.stop
            >
              {{ employeeData?.work_phone_number }} <span class="text-medium-emphasis">- рабочий</span>
            </a>
            <span v-else class="body-1">Рабочий: Не указано</span>
          </div>
        </div>

        <!-- Chat Button -->
        <VBtn
          block
          prepend-icon="ri-chat-1-line"
          variant="outlined"
          @click="handleChat"
        >
          Написать в чате
        </VBtn>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.employee-dialog :deep(.v-overlay__content) {
  position: fixed !important;
  right: 0 !important;
  top: 0 !important;
  bottom: 0 !important;
  margin: 0 !important;
  max-width: none !important;
  max-height: none !important;
  height: 100% !important;
}

.employee-sidebar {
  border-radius: 0 !important;
  margin-left: auto !important;
  height: 100% !important;
  min-height: 100% !important;
}

.title {
  text-transform: uppercase;
  color: rgb(var(--v-theme-text-primary), var(--v-disabled-opacity)) !important;
}

.subtitle {
  color: rgb(var(--v-theme-text-primary), var(--medium-emphasis-opacity)) !important;
}

.contacts {
  color: rgb(var(--v-theme-text-primary), var(--high-emphasis-opacity)) !important;
}
</style>
